import React, { useEffect, useMemo, useState } from "react";
import MetabaseFrame from "./MetabaseFrame";
import MetabaseServices from "../../services/axiosServices/apiServices/MetabaseServices";

const MAX_FRAMES = 8;
const MIN_FRAMES = 1;

const getGridClassName = (count) => {
  if (count <= 1) {
    return "grid-cols-1";
  }

  if (count === 2) {
    return "grid-cols-1 lg:grid-cols-2";
  }

  if (count <= 4) {
    return "grid-cols-1 md:grid-cols-2";
  }

  if (count <= 6) {
    return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3";
  }

  return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4";
};

function MetabaseDashboard(props) {
  const { title, frames = [], frameCount, className = "", onFramesResolved } = props;
  const metabaseServices = useMemo(() => new MetabaseServices(), []);
  const [resolvedFrames, setResolvedFrames] = useState([]);
  const [isResolvingFrames, setIsResolvingFrames] = useState(false);

  const safeFrameCount = Math.max(
    MIN_FRAMES,
    Math.min(MAX_FRAMES, Number(frameCount) || frames.length || MIN_FRAMES)
  );

  const safeFrames = Array.isArray(frames) ? frames.slice(0, MAX_FRAMES) : [];
  const visibleFrames = useMemo(
    () => safeFrames.slice(0, safeFrameCount),
    [safeFrames, safeFrameCount]
  );
  const frameSignature = JSON.stringify(visibleFrames);

  const buildRequest = (frame) => {
    if (frame.request) {
      return {
        ...frame.request,
        expMinutes: frame.request.expMinutes ?? frame.expMinutes ?? 10,
        bordered: frame.request.bordered ?? frame.bordered ?? false,
        titled: frame.request.titled ?? frame.titled ?? false,
      };
    }

    if (!frame.questionId && !frame.dashboardId) {
      return null;
    }

    const parameters = frame.parameters || frame.params || {};
    const parameterString =
      frame.parameterString ||
      Object.keys(parameters)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(parameters[key])}`
        )
        .join("&");

    return {
      questionId: frame.questionId || 0,
      dashboardId: frame.dashboardId || 0,
      parameters,
      params: parameters,
      parameterString,
      expMinutes: frame.expMinutes ?? 10,
      bordered: frame.bordered ?? false,
      titled: frame.titled ?? false,
    };
  };

  const extractIframeUrl = (response) => {
    if (!(response && response.statusCode === 200 && response.responseItem)) {
      return "";
    }

    const responseContent = response.responseItem.responseContent;
    if (typeof responseContent === "string") {
      return responseContent;
    }

    return (
      (responseContent &&
        (responseContent.iframeUrl ||
          responseContent.url ||
          responseContent.embedUrl)) ||
      ""
    );
  };

  useEffect(() => {
    let isMounted = true;

    const resolveFrames = async () => {
      const hasApiFrames = visibleFrames.some(
        (frame) => !frame.src && (frame.questionId || frame.dashboardId || frame.request)
      );

      if (!hasApiFrames) {
        setResolvedFrames(visibleFrames);
        setIsResolvingFrames(false);
        if (typeof onFramesResolved === "function") {
          onFramesResolved(visibleFrames);
        }
        return;
      }

      setIsResolvingFrames(true);

      const results = await Promise.all(
        visibleFrames.map(async (frame) => {
          if (frame.src) {
            return frame;
          }

          const request = buildRequest(frame);
          if (!request) {
            return { ...frame, src: frame.fallbackUrl || "", titled: false };
          }

          try {
            const response = await metabaseServices.getMetabaseIframeUrl(request);
            const iframeUrl = extractIframeUrl(response);
            if (iframeUrl) {
              if (!request.parameterString) {
                return { ...frame, src: iframeUrl };
              }
              let baseUrl = iframeUrl;
              let hash = "";

              if (iframeUrl.includes("#")) {
                const parts = iframeUrl.split("#");
                baseUrl = parts[0];
                hash = `#${parts[1]}`;
              }

              const separator = baseUrl.includes("?") ? "&" : "?";

              return {
                ...frame,
                src: `${baseUrl}${separator}${request.parameterString}${hash}`,
              };

            }
            return { ...frame, src: frame.fallbackUrl || "" };
          } catch (error) {
            return { ...frame, src: frame.fallbackUrl || "" };
          }
        })
      );

      if (isMounted) {
        setResolvedFrames(results);
        setIsResolvingFrames(false);
        if (typeof onFramesResolved === "function") {
          onFramesResolved(results);
        }
      }
    };

    resolveFrames();

    return () => {
      isMounted = false;
    };
  }, [frameSignature, metabaseServices, onFramesResolved]);

  const framesToRender = resolvedFrames.length > 0 ? resolvedFrames : visibleFrames;
  const gridClassName = getGridClassName(framesToRender.length);

  return (
    <div className="w-full">
      {title ? (
        <div className="mb-4">
          <h2 className="text-xl font-semibold theme-color">{title}</h2>
        </div>
      ) : null}

      {framesToRender.length > 0 ? (
        <div className={`grid gap-6 ${gridClassName}`}>
          {framesToRender.map((frame, index) => (
            <MetabaseFrame
              key={frame.id || frame.title || `metabase-frame-${index}`}
              title={frame.title}
              src={frame.src}
              className={className}
              height={frame.height || 420}
              allowFullScreen={frame.allowFullScreen !== false}
              loading={frame.loading || "lazy"}
              isLoading={isResolvingFrames && !frame.src}
              showDownloadButton={frame.showDownloadButton === true}
              downloadLabel={frame.downloadLabel || "Download Results"}
              downloadUrl={frame.downloadUrl || ""}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#e5e7eb] rounded-md p-6 text-[#6b7280] text-sm">
          No Metabase frames configured
        </div>
      )}
    </div>
  );
}

export default MetabaseDashboard;
